/* ============================================================
   GOOTER Tracking — consent-gated dataLayer for GA4 / Meta /
   TikTok / Google Ads. No purchase event in theme — that runs
   via Shopify Customer Events / Custom Pixel.

   Architecture:
   - window.gooterCtx       (page context, set by gooter-tracking-data.liquid)
   - window.gooterItem      (current product if any)
   - window.gooterList      (current collection if any)
   - window.gooterCart      (cart snapshot if not empty)
   - window.dataLayer       (GTM/GA4 push target)
   - window.gooterDL        (internal buffer if dataLayer not ready)

   Consent gate:
   Marketing/analytics events only fire when localStorage flag
   `gooter_consent_v1.analytics` (or `.marketing`) is "1".
   Required events (page_view_custom, view_item_custom) always
   fire as technical aggregates with no PII.

   UTM / click-id capture:
   On first hit, captures utm_*, gclid, fbclid, ttclid, msclkid
   plus landing_page, referrer, first_seen_at into
   localStorage `gooter_attribution_v1`. Last-click overrides
   into `gooter_attribution_last_v1`.
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_FIRST = 'gooter_attribution_v1';
  var STORAGE_LAST  = 'gooter_attribution_last_v1';
  var CONSENT_KEY   = 'gooter_consent_v1';
  var TTL_DAYS      = 90;

  function getConsent() {
    try {
      var raw = localStorage.getItem(CONSENT_KEY);
      if (!raw) return { analytics: false, marketing: false, functional: true, required: true };
      var c = JSON.parse(raw);
      return {
        required:   true,
        functional: !!c.functional,
        analytics:  !!c.analytics,
        marketing:  !!c.marketing
      };
    } catch (e) { return { analytics: false, marketing: false, functional: true, required: true }; }
  }

  function readQuery() {
    var params = {};
    var q = window.location.search.substring(1);
    if (!q) return params;
    q.split('&').forEach(function (p) {
      var eq = p.indexOf('=');
      if (eq === -1) return;
      var k = decodeURIComponent(p.slice(0, eq));
      var v = decodeURIComponent(p.slice(eq + 1));
      params[k] = v;
    });
    return params;
  }

  function captureAttribution() {
    var q = readQuery();
    var keys = ['utm_source','utm_medium','utm_campaign','utm_content','utm_term','gclid','fbclid','ttclid','msclkid'];
    var current = {};
    keys.forEach(function (k) { if (q[k]) current[k] = q[k]; });

    // Always store last-click
    if (Object.keys(current).length) {
      current.landing_page = window.location.pathname;
      current.referrer = document.referrer || '';
      current.seen_at = Date.now();
      try { localStorage.setItem(STORAGE_LAST, JSON.stringify(current)); } catch (e) {}
    }

    // First-click only if not set or expired
    var existing = null;
    try { existing = JSON.parse(localStorage.getItem(STORAGE_FIRST) || 'null'); } catch (e) {}
    var ttlMs = TTL_DAYS * 24 * 60 * 60 * 1000;
    var expired = !existing || (Date.now() - (existing.seen_at || 0) > ttlMs);
    if (Object.keys(current).length && expired) {
      var first = Object.assign({}, current);
      first.first_seen_at = Date.now();
      try { localStorage.setItem(STORAGE_FIRST, JSON.stringify(first)); } catch (e) {}
    }
  }

  function getAttribution() {
    var first = {}, last = {};
    try { first = JSON.parse(localStorage.getItem(STORAGE_FIRST) || '{}'); } catch (e) {}
    try { last  = JSON.parse(localStorage.getItem(STORAGE_LAST)  || '{}'); } catch (e) {}
    return {
      first_utm_source:   first.utm_source   || '',
      first_utm_medium:   first.utm_medium   || '',
      first_utm_campaign: first.utm_campaign || '',
      first_gclid:        first.gclid        || '',
      first_fbclid:       first.fbclid       || '',
      first_ttclid:       first.ttclid       || '',
      last_utm_source:    last.utm_source    || '',
      last_utm_medium:    last.utm_medium    || '',
      last_utm_campaign:  last.utm_campaign  || '',
      last_gclid:         last.gclid         || '',
      last_fbclid:        last.fbclid        || '',
      last_ttclid:        last.ttclid        || ''
    };
  }

  function newEventId() {
    return 'gtr_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
  }

  function push(eventName, payload, options) {
    options = options || {};
    var consent = getConsent();
    // Marketing/analytics-only events are skipped without consent
    if (options.requiresConsent && !consent.analytics && !consent.marketing) return;

    var ctx = window.gooterCtx || {};
    var attr = getAttribution();
    var evt = Object.assign({
      event: eventName,
      event_id: newEventId(),
      page_type: ctx.page_type || '',
      template: ctx.template || '',
      language: ctx.language || '',
      market: ctx.market || '',
      currency: ctx.currency || '',
      timestamp: Date.now()
    }, attr, payload || {});

    // GTM / GA4 target
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(evt);
    // Internal buffer for debug
    window.gooterDL = window.gooterDL || [];
    window.gooterDL.push(evt);
  }

  // ===== Auto-emit =====
  function emitPageView() {
    push('page_view_custom', {}, { requiresConsent: false });
  }
  function emitViewItem() {
    if (!window.gooterItem) return;
    push('view_item_custom', {
      items: [window.gooterItem],
      value: parseFloat(window.gooterItem.price || 0),
      currency: window.gooterItem.currency || (window.gooterCtx || {}).currency
    }, { requiresConsent: true });
  }
  function emitViewList() {
    if (!window.gooterList) return;
    push('view_item_list_custom', { list: window.gooterList }, { requiresConsent: true });
  }
  function emitViewCart() {
    if (!window.gooterCart) return;
    push('view_cart_custom', {
      items: window.gooterCart.items,
      value: parseFloat(window.gooterCart.total_price || 0),
      currency: window.gooterCart.currency
    }, { requiresConsent: true });
  }

  // ===== Interaction listeners =====
  document.addEventListener('click', function (e) {
    var ctx = window.gooterCtx || {};

    // Add-to-cart buttons
    var atc = e.target.closest('[name="add"], .product-form__submit, [data-gooter-upsell-add]');
    if (atc) {
      var section = atc.closest('[data-section-id], [data-section]') || {};
      var item = window.gooterItem || null;
      // Upsell card override
      var card = atc.closest('[data-gooter-upsell-card]');
      if (card) {
        item = {
          item_id: card.getAttribute('data-product-id') || '',
          item_name: card.getAttribute('data-product-title') || atc.getAttribute('data-product-title') || '',
          product_handle: card.getAttribute('data-product-handle') || '',
          item_variant: card.getAttribute('data-variant-id') || ''
        };
        push('upsell_click_custom', { items: [item], placement: card.getAttribute('data-placement') || '' }, { requiresConsent: true });
      }
      if (item) {
        push('add_to_cart_custom', {
          items: [item],
          value: parseFloat(item.price || 0),
          currency: (window.gooterCtx || {}).currency,
          source_section: section.id || section.dataset && section.dataset.section || ''
        }, { requiresConsent: true });
      }
      return;
    }

    // Checkout click
    var co = e.target.closest('[name="checkout"], #checkout, .cart__checkout-button');
    if (co) {
      snapshotPendingPurchase();
      push('begin_checkout_click_custom', {
        items: (window.gooterCart && window.gooterCart.items) || [],
        value: parseFloat((window.gooterCart || {}).total_price || 0),
        currency: (window.gooterCart || {}).currency
      }, { requiresConsent: true });
      return;
    }

    // Phone / mail / WhatsApp
    var tel = e.target.closest('a[href^="tel:"]');
    if (tel) { push('phone_click_custom', { href: tel.getAttribute('href') }, { requiresConsent: false }); return; }
    var mail = e.target.closest('a[href^="mailto:"]');
    if (mail) { push('email_click_custom', { href: mail.getAttribute('href') }, { requiresConsent: false }); return; }

    // Quiz / Product Finder
    if (e.target.closest('[data-quiz-open]')) { push('product_finder_start_custom', {}, { requiresConsent: true }); return; }
    if (e.target.closest('.gquiz__result-cta')) { push('product_finder_result_click_custom', { href: e.target.closest('a').getAttribute('href') }, { requiresConsent: true }); return; }

    // Newsletter signup form submit handled via submit event below.
  }, true);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form && form.action && /\/contact#newsletter|customers\/customer|newsletter/.test(form.action)) {
      push('newsletter_signup_custom', {}, { requiresConsent: true });
    }
  }, true);

  // ===== Consent Mode v2 defaults (must run before any tag) =====
  function initConsentDefaults() {
    try {
      window.gtag = window.gtag || function () { (window.dataLayer = window.dataLayer || []).push(arguments); };
      // Defaults: denied — cookie banner Accept upgrades to granted via gooter-cookie-consent.js
      var existing = null;
      try { existing = JSON.parse(localStorage.getItem('gooter_consent_v1') || 'null'); } catch (e) {}
      var c = existing && existing.c ? existing.c : { analytics: false, marketing: false, functional: false };
      window.gtag('consent', 'default', {
        ad_storage:           c.marketing ? 'granted' : 'denied',
        analytics_storage:    c.analytics ? 'granted' : 'denied',
        ad_user_data:         c.marketing ? 'granted' : 'denied',
        ad_personalization:   c.marketing ? 'granted' : 'denied',
        functionality_storage: c.functional ? 'granted' : 'denied',
        personalization_storage: c.functional ? 'granted' : 'denied',
        security_storage:     'granted',
        wait_for_update:      500
      });
    } catch (e) {}
  }

  // ===== Pending purchase snapshot =====
  // When user clicks begin_checkout, snapshot the cart so that if they return
  // to the storefront after order completion (rare but possible), we can fire
  // Purchase as a best-effort fallback. The CANONICAL Purchase event still
  // requires Customer Events Custom Pixel (paste docs/shopify-customer-events-gtm-pixel.js)
  // or the official Meta / Google / TikTok Shopify apps.
  function snapshotPendingPurchase() {
    if (!window.gooterCart) return;
    try {
      localStorage.setItem('gooter_pending_purchase_v1', JSON.stringify({
        items: window.gooterCart.items,
        value: window.gooterCart.total_price,
        currency: window.gooterCart.currency,
        ts: Date.now()
      }));
    } catch (e) {}
  }

  function detectReturnFromCheckout() {
    // Heuristic: URL contains "thank_you" OR "?order_id" OR document.referrer is *.myshopify.com/checkouts*
    var url = window.location.href;
    var ref = document.referrer || '';
    var isThankYou = /thank[_\-]?you|order[_\-]?status|order[_\-]?confirmation/i.test(url) ||
                     /[?&]order_id=|[?&]order_number=/i.test(url) ||
                     /shopify\.com\/checkouts/i.test(ref);
    if (!isThankYou) return;

    var snap = null;
    try { snap = JSON.parse(localStorage.getItem('gooter_pending_purchase_v1') || 'null'); } catch (e) {}
    if (!snap || !snap.items || !snap.items.length) return;
    var ageMs = Date.now() - (snap.ts || 0);
    if (ageMs > 30 * 60 * 1000) {
      try { localStorage.removeItem('gooter_pending_purchase_v1'); } catch (e) {}
      return;
    }

    // De-dupe: same transaction reported within last hour gets skipped
    var seenKey = 'gooter_purchase_seen_' + (snap.ts || 0);
    if (sessionStorage.getItem(seenKey)) return;
    sessionStorage.setItem(seenKey, '1');

    push('purchase_fallback_custom', {
      items: snap.items,
      value: parseFloat(snap.value || 0),
      currency: snap.currency,
      source: 'storefront_return',
      note: 'Best-effort fallback. Canonical event is via Customer Events Pixel.'
    }, { requiresConsent: true });

    try { localStorage.removeItem('gooter_pending_purchase_v1'); } catch (e) {}
  }

  // ===== Boot =====
  function boot() {
    initConsentDefaults();
    captureAttribution();
    emitPageView();
    if ((window.gooterCtx || {}).page_type === 'product') emitViewItem();
    if ((window.gooterCtx || {}).page_type === 'collection') emitViewList();
    if ((window.gooterCtx || {}).page_type === 'cart') emitViewCart();
    detectReturnFromCheckout();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose for debug / external pushes
  window.gooterTrack = push;
})();
